// Copyright (c) Ping. All rights reserved.
//
// Apply inbound `com.ping.group_name` payloads to local group rows.

import { getCurrentUserAccountId } from './registerCurrentUser';
import type { ConversationRecord } from './persistenceAdapters';

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

/** Merge a group_name envelope payload into a conversation patch. */
export function groupNamePayloadToConversationPatch(
  payload: unknown,
): Partial<ConversationRecord> | null {
  if (!isRecord(payload)) return null;
  const title = typeof payload.name === 'string' ? payload.name.trim() : '';
  if (title.length === 0) return null;

  const patch: Partial<ConversationRecord> = {
    kind: 'group',
    name: title,
  };

  // Group avatar id. iOS/web propagate the group picture via the MLS group meta
  // (`avatarMediaId` in the GroupContext name), but a web JOINER can't read that
  // meta (`getConversation().name` is empty), so the avatar id must ALSO ride
  // this broadcast — the only group-identity channel that reaches web. The host
  // resolves it to a URL in `hydrateMemberProfiles`.
  const avatarMediaId =
    typeof payload.avatarMediaId === 'string' && payload.avatarMediaId.trim().length > 0
      ? payload.avatarMediaId.trim()
      : undefined;
  if (avatarMediaId !== undefined) {
    patch.avatarMediaId = avatarMediaId;
  }

  const localAccountId = getCurrentUserAccountId();
  const idsRaw = payload.memberAccountIds;
  if (Array.isArray(idsRaw)) {
    const peerIds = idsRaw
      .filter((id): id is string => typeof id === 'string' && id.length > 0)
      .filter((id) => localAccountId === null || id !== localAccountId)
      .sort();
    if (peerIds.length > 0) {
      patch.memberAccountIds = peerIds;
    }
  }

  const namesRaw = payload.memberDisplayNames;
  if (isRecord(namesRaw)) {
    const memberDisplayNames: Record<string, string> = {};
    for (const [accountId, label] of Object.entries(namesRaw)) {
      if (typeof label === 'string' && label.length > 0) {
        memberDisplayNames[accountId] = label;
      }
    }
    if (Object.keys(memberDisplayNames).length > 0) {
      patch.memberDisplayNames = memberDisplayNames;
    }
  }

  return patch;
}
